<template>
  <form class="address-editor" @submit.prevent="submit">
    <div class="address-editor__grid">
      <div class="address-editor__field">
        <label class="address-editor__label" for="address-name">Nombre</label>
        <input
          id="address-name"
          v-model.trim="form.name"
          class="address-editor__input"
          type="text"
          placeholder="Casa matriz, sucursal..."
        >
      </div>

      <div class="address-editor__field address-editor__field--full">
        <label class="address-editor__label" for="address-line">Direccion</label>
        <input
          id="address-line"
          v-model.trim="form.address"
          class="address-editor__input"
          type="text"
          placeholder="Direccion completa"
        >
      </div>

      <div class="address-editor__field address-editor__field--full">
        <label class="address-editor__label" for="address-comuna">Comuna</label>
        <select
          id="address-comuna"
          v-model="form.comuna_id"
          class="address-editor__input"
        >
          <option disabled value="0">Seleccione una comuna</option>
          <option v-for="comuna in comunas" :key="comuna.id" :value="comuna.id">
            {{ comuna.name }}
          </option>
        </select>
      </div>
    </div>

    <p v-if="error" class="address-editor__error">{{ error }}</p>

    <footer class="address-editor__actions">
      <button class="address-editor__ghost" type="button" :disabled="saving" @click="$emit('cancel')">
        Cancelar
      </button>

      <button class="address-editor__primary" type="submit" :disabled="saving">
        {{ saving ? 'Guardando...' : submitLabel }}
      </button>
    </footer>
  </form>
</template>

<script>
import companyOperationsService from '@/services/company/companyOperations.service'

const defaultForm = () => ({
  id: null,
  name: '',
  address: '',
  comuna_id: 0
})

export default {
  name: 'AddressEditor',
  props: {
    value: {
      type: Object,
      default: null
    },
    companyId: {
      type: [String, Number],
      required: true
    },
    comunas: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      form: defaultForm(),
      saving: false,
      error: ''
    }
  },
  computed: {
    submitLabel () {
      return this.form.id ? 'Guardar cambios' : 'Crear dirección'
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (newValue) {
        this.form = {
          ...defaultForm(),
          ...(newValue || {})
        }
        if (!this.form.comuna_id) {
          this.form.comuna_id = 0
        }
      }
    }
  },
  methods: {
    validate () {
      if (!this.form.name) {
        this.error = 'El nombre es obligatorio.'
        return false
      }
      if (!this.form.address) {
        this.error = 'La dirección es obligatoria.'
        return false
      }
      if (!this.form.comuna_id || Number(this.form.comuna_id) === 0) {
        this.error = 'Debes seleccionar una comuna.'
        return false
      }
      this.error = ''
      return true
    },
    normalizePayload () {
      return {
        id: this.form.id,
        company_id: Number(this.companyId),
        name: this.form.name,
        address: this.form.address,
        comuna_id: Number(this.form.comuna_id)
      }
    },
    async submit () {
      if (!this.validate()) return

      this.saving = true
      this.error = ''

      try {
        const payload = this.normalizePayload()
        const saved = this.form.id
          ? await companyOperationsService.updateAddress(payload)
          : await companyOperationsService.createAddress(payload)

        this.$emit('saved', saved)
      } catch (err) {
        this.error = err && err.message ? err.message : 'No fue posible guardar la dirección.'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.address-editor {
  display: grid;
  gap: 1rem;
}

.address-editor__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
}

.address-editor__field {
  display: grid;
  gap: 0.38rem;
}

.address-editor__field--full {
  grid-column: span 2;
}

.address-editor__label {
  color: #4b3b2f;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.address-editor__input {
  min-height: 2.9rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid rgba(102, 81, 57, 0.14);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.76);
  color: #241c16;
}

.address-editor__error {
  margin: 0;
  color: #a24444;
  font-size: 0.9rem;
}

.address-editor__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.55rem;
}

.address-editor__ghost,
.address-editor__primary {
  min-height: 2.65rem;
  padding: 0 0.95rem;
  border-radius: 999px;
}

.address-editor__ghost {
  border: 1px solid rgba(102, 81, 57, 0.12);
  background: rgba(255, 255, 255, 0.72);
  color: #392d22;
}

.address-editor__primary {
  border: 0;
  background: linear-gradient(135deg, #251d16 0%, #4c3c2d 100%);
  color: #f7f0e5;
}

@media (max-width: 640px) {
  .address-editor__grid {
    grid-template-columns: 1fr;
  }

  .address-editor__field--full {
    grid-column: span 1;
  }

  .address-editor__actions {
    flex-direction: column;
  }
}
</style>
